import { DeviceTypeConfigurations, doesDeviceVersionMatchSelector, type FirstPartyDeviceType } from "verdi-common";
import { DeviceVersion } from "verdi-common/schemas/commonSubSchemas/deviceVersion";
import { Gen4NetworkInfo } from "verdi-common/schemas/device/subSchemas/gen4NetworkInfo";
import { SproutAutomationSettings } from "verdi-common/schemas/device/subSchemas/sproutAutomationSettings";
import { SproutNetworkInfo } from "verdi-common/schemas/device/subSchemas/sproutNetworkInfo";
import { SproutVerificationInfo } from "verdi-common/schemas/device/subSchemas/sproutVerification/sproutVerificationInfo.js";
import * as APICommands from "../../APICommands/index.js";
import { generateShortCodeFromEui } from "verdi-common/utils/shortCodes/EuiShortCodeMapper.js";
import { DeviceBase } from "./DeviceBase.js";
import { IrrigationDeviceSensor } from "./IrrigationDeviceSensor.js";
import { DeviceWarning } from "./Warnings.js";
import { ValveState } from "verdi-common/schemas/commonSubSchemas/valveState.js";
import { VerificationCheckConfiguration } from "verdi-common/schemas/device/subSchemas/verificationCheckConfiguration";
import { SignalMetrics } from "verdi-common/schemas/device/subSchemas/signalMetrics";

export class IrrigationDeviceBase extends DeviceBase {
    /**
     * Bottom zone (V1), top zone  (V2) valve states.
     * @type {("?" | "open" | "closed")[]}
     */
    valveStates: ("?" | "open" | "closed")[] = [];
    /**
     *
     * @type {(IrrigationDeviceSensor | undefined)[]}
     */
    connectedSensors: (IrrigationDeviceSensor | undefined)[] = [];
    /**
     * The ID on the QR code for the device
     * @type {string | undefined}
     */
    enclosureID;
    /**
     *
     * @type {{prevFrameCounts: {
                down:  Number,
                up: Number,
            },
            sentDownlinkFrame:  Number,
            nextUplinkNeedingResponse:  Number,
            nextUplinkTime: Date,
            lastTimeUpate: Date,
            DormantMode: Boolean,
            lastUplinkTime:  Date,
            lastDownlinkTime: Date,
            lastValidatedDownlink: Number,
            nodeUplinkTimingOffset: Number}}
     */
    uplinkInfo = {};
    /**
     * The actual DBID of the device, not the node ID
     * @type {string}
     */
    rawDBID = "";
    name: string;
    labelText = "";
    sproutIrrigationVerification?: SproutVerificationInfo;
    sproutAutomationSettings?: SproutAutomationSettings;
    sproutNetworkInfo?: SproutNetworkInfo;
    gen4NetworkInfo?: Gen4NetworkInfo;
    connectedZoneIDs: string[] = [];

    valveState?: ValveState;

    lastValveStateChange?: Date;

    lastContact?: Date;

    /*
     * When possible, make this reference the verdi-common definition
     * and make the logic that assigns it (near the end of loadFromJSON())
     * based on the device document.
     */
    deviceVersionInfo: DeviceVersion;

    verificationCheckConfigurations: VerificationCheckConfiguration[] = [];

    // aliased so that it matches the actual device model
    get version(): DeviceVersion {
        return this.deviceVersionInfo;
    }

    constructor(json) {
        super(json);
        this.category = "irrigationDevice";
        this.dbID = json.node_id;
        this.rawDBID = json._id;
        this.assignFromJSON(json);
        if (Array.isArray(json.connectedZones)) {
            this.connectedZoneIDs = json.connectedZones || [];
            if (json.zone) {
                this.connectedZoneIDs[0] = json.zone;
            }
        }
    }

    /**
     * refreshes this device properties from the server
     * @returns {Promise<void>}
     */
    async refresh() {
        this.assignFromJSON(await APICommands.DeviceAPI.getOne(this.id));
        this.lastRefreshed = new Date(Date.now());
        this.onChange.trigger({});
    }

    supportsSettingManualMode = false;

    supportsNetworkTests = false;

    async conductNetworkTest() {
        if (!this.supportsNetworkTests) {
            throw new Error("Invalid device for network test. Only some devices support network tests");
        }
        const json = await APICommands.DeviceAPI.conductNetworkTestOnOne(this.id);
        this.lastRefreshed = new Date(Date.now());
        this.assignFromJSON(json);
        this.onChange.trigger({});
        return this;
    }

    /**
     *
     * @param {"open" | "closed" | "none"} manualMode
     * @returns {Promise<this>}
     */
    async setManualMode(manualMode) {
        // if it's before generation 3 it doesn't support setting manual mode
        if (!this.supportsSettingManualMode) {
            throw new Error("Invalid device for setting manual mode!");
        }
        const json = await APICommands.DeviceAPI.setRemoteManualModeOnOne(this.id, manualMode);
        this.lastRefreshed = new Date(Date.now());
        this.assignFromJSON(json);
        this.onChange.trigger({});
        return this;
    }
    assignFromJSON(json) {
        super.assignFromJSON(json);

        try {
            if (json.warnings) {
                this.warnings = json.warnings.map((w) => new DeviceWarning(w));
            }
            this.warnings.sort((a, b) => {
                const aScore = a.warningLevel * 10000 + a.warningTypePriority;
                const bScore = b.warningLevel * 10000 + b.warningTypePriority;
                return bScore - aScore;
            });
        } catch (e) {
            console.warn(e);
        }
        if (json.sproutAutomationSettings) {
            this.sproutAutomationSettings = json.sproutAutomationSettings;
        }
        if (json.sproutIrrigationVerification) {
            this.sproutIrrigationVerification = json.sproutIrrigationVerification;
        }
        const oldValveStates = this.valveStates;
        this.valveStates = ["?", "?"];
        if (json.valveStates) {
            this.valveStates = json.valveStates;
        } else if (this.recentData["valveState"]) {
            this.valveStates[0] = this.recentData["valveState"].value % 2 ? "open" : "closed";
            this.valveStates[1] = Math.floor(this.recentData["valveState"].value / 2) % 2 ? "open" : "closed";
        } else {
            this.valveStates = oldValveStates || ["?", "?"];
        }
        if (json.valveState) {
            this.valveState = json.valveState;
        }
        if (json.lastValveStateChange) {
            this.lastValveStateChange = new Date(json.lastValveStateChange.valueOf());
        }
        if (json.sproutNetworkInfo) {
            this.sproutNetworkInfo = json.sproutNetworkInfo || this.sproutNetworkInfo;
        }
        if (json.gen4NetworkInfo) {
            this.gen4NetworkInfo = json.gen4NetworkInfo || this.gen4NetworkInfo;
        }

        if (json.EnclosureID) {
            this.enclosureID = json.EnclosureID || this.enclosureID;
        }
        if (json.enclosureID) {
            this.enclosureID = json.enclosureID || this.enclosureID;
        }
        this.device_stats = json.device_stats || this.device_stats || {};
        this.device_stats.batt = json.RunningAverageVoltage || this.device_stats?.batt || 0;

        this.uplinkInfo = json.uplinkInfo || this.uplinkInfo;
        if (json.status) {
            if (json.status.toLowerCase() === "active") {
                this.status = "stand by";
            } else if (json.status.toLowerCase() === "error") {
                this.status = "error";
            } else {
                this.status = json.status;
            }
        } else {
            this.status = this.status || "idle";
        }
        if (json.DeviceType) {
            this.type = json.DeviceType;
        } else {
            this.type = this.type || "beta1v";
        }
        if (json.connectedSensors && Array.isArray(json.connectedSensors)) {
            this.connectedSensors = json.connectedSensors.map((j) => {
                const newSensor = new IrrigationDeviceSensor(j || {});
                this.loadPromise.then(() => {
                    if (this.recentData[newSensor.dataType]) {
                        newSensor.lastDataPoint = this.recentData[newSensor.dataType];
                    }
                });
                return newSensor;
            });
        } else if (["sap_1v", "sap_2v", "sap_sense"].includes(this.type)) {
            this.connectedSensors = [undefined, undefined];
        }

        this.deviceVersionInfo = {
            schemaKey: DeviceVersion.schemaKey,
            generation:
                json.version?.generation ??
                {
                    seed: 1,
                    sap: 2,
                    sprout: 3,
                    gen4: 4,
                }[DeviceTypeConfigurations[this.type as FirstPartyDeviceType].generation],
            productLine: json.version?.productLine ?? "?",
            hardwareVersion: json.version?.hardwareVersion ?? -1,
            firmwareVersion: json.version?.firmwareVersion ?? -1,
            hardwareVariant: json.version?.hardwareVariant ?? "a",
        };

        this.verificationCheckConfigurations = json.verificationCheckConfigurations ?? [];

        this.lastContact = new Date(json.lastContact);

        this.supportsSettingManualMode = doesDeviceVersionMatchSelector(
            this.version,
            "(4+.*.*.*.*) (3.micro.*.*.1+) (3.block.*.*.3+)",
        );
        this.supportsNetworkTests = doesDeviceVersionMatchSelector(this.version, "3+.*.*.*.*");

        this.labelText = this.id;
        if (this.verdiId) {
            this.labelText = this.verdiId;
        } else if (this.dbID?.includes("eui")) {
            try {
                this.labelText = generateShortCodeFromEui(this.id);
            } catch (e) {
                console.warn("Error generating label text");
                console.warn(e);
            }
        } else if (this.enclosureID) {
            this.labelText = this.enclosureID;
        }
    }

    /**
     * Helper to extract best signal metrics from a SignalMetrics array
     * Returns the gateway with the highest SNR value
     */
    getBestSignalMetrics(): SignalMetrics | undefined {
        const networkInfo = this.gen4NetworkInfo || this.sproutNetworkInfo;
        const metrics = networkInfo?.lastSignalMetrics;

        if (!metrics || metrics.length === 0) return undefined;

        const bestSignalMetrics = metrics.reduce((bestSignal, current) =>
            current.snr > bestSignal.snr ? current : bestSignal,
        );

        return bestSignalMetrics;
    }
}
